/**
 * Die Lobby — §48, §67 Schritte 16 bis 18.
 *
 * **Eine Form, zwei Ansichten** (Nr. 27): dieselbe Komponente zeigt dem Host Konfiguration und
 * Lobby in einem, dem Mitspieler dieselben Daten ohne Bedienelemente. Das ist keine Sparsamkeit,
 * sondern folgt aus der Schnittstelle: `lobbyViewSchema` hat nur **eine** Form, weil die Lobby
 * nichts enthält, was vor einem Beteiligten verborgen wäre. Die Geheimhaltung aus §45 betrifft
 * die Aufstellung, nicht die Lobby.
 *
 * **`canStart` kommt vom Server und wird hier nicht gerechnet.** Ob gestartet werden darf, ist
 * eine Regel — jeder Platz belegt (Nr. 28), mindestens zwei Seiten (Nr. 24) —, und Regeln
 * entscheidet der Server (`CLAUDE.md` Regel 1). Eine Lobby, die voll *aussieht*, gibt den
 * Startknopf deshalb nicht frei; der Client darf anzeigen, was möglich ist, nie was gültig ist.
 *
 * **`connected` kommt aus `PRESENCE` und nicht aus der Lobby-Nachricht.** Eine Verbindung kommt
 * und geht, ohne dass sich die Lobby ändert; die ganze Ansicht dafür erneut zu schicken wäre
 * Verschwendung. Der Wert **in** der Lobby ist nur der Stand im Moment des Versands — hier gilt
 * der laufend fortgeschriebene.
 *
 * **Ein getrennter Spieler behält seinen Platz** (§45, §48). Er wird als getrennt gezeigt, nicht
 * entfernt: ein Netzausfall darf niemanden aus seiner Partie werfen (§46).
 *
 * **Ein Bot wird am freien Farbplatz eingesetzt** (`V2-2f2`, **E64**; die Wahl seit `L2` in
 * `BotChooser.tsx`, **E110**) — dort, wo „Zuordnen",
 * „Farbe lösen" und „Entfernen" schon stehen. Entscheidend war der häufigste Fall: *„wir sind
 * nur zu zweit, der dritte kommt nicht."* Und für ihn **entfällt die Verbindungsanzeige
 * ersatzlos**: ein Bot hat nie einen WebSocket, `connected` ist also immer `false` — in der
 * Sichtprüfung von `V2-2e3` stand „Robo · Grün · nicht verbunden", **während er zog**.
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  BotChoice,
  LobbyPlayerDto,
  LobbyView,
  SectorIdDto,
} from '@flaghex/contracts';

import { ConfirmButton } from '../game/ConfirmButton.js';
import { BotBadge } from './BotBadge.js';
import { BotChooser } from './BotChooser.js';

export interface HostControls {
  readonly onAssign: (playerId: string, sector: SectorIdDto | null) => void;
  readonly onRemove: (playerId: string) => void;
  readonly onStart: () => void;
  /**
   * Einen Bot auf einen freien Farbplatz setzen (**E64**, seit `L2` mit der **Wahl**).
   *
   * **Farbe, Name und Wahl**, alles von hier: die Farbe ist der Platz, an dem der Knopf steht
   * (Antwort 1), den Namen tippt der Host (Antwort 3), die Wahl trifft er im Formular (**E110**).
   * Bis `L2` gab es keine Wahl — „solange `STRATEGY_IDS` nur `random` enthält, wäre ein
   * Auswahlfeld Ballast" (Folge 2); diese Begründung ist seit `V2-4b` verfallen.
   */
  readonly onAddBot: (sector: SectorIdDto, displayName: string, choice: BotChoice) => void;
  /** Die Partie schließen, für alle (`R12`) — der zweite Klick bestätigt. */
  readonly onClose: () => void;
}

/**
 * Was Angaben voneinander trennt.
 *
 * Keine Kosmetik: ohne Trennung stand im Browser „Gelb · Anna Farbe lösen Entfernen" in einem
 * Zug — derselbe Befund wie in Zyklus 10b und 10d, und wieder bei vollständig grüner Suite.
 */
const SEPARATOR = ' \u00b7 ';

export interface LobbyPanelProps {
  readonly lobby: LobbyView;
  /**
   * Wer gerade verbunden ist — fortgeschrieben aus `PRESENCE`.
   *
   * **Fehlt es, gilt der Stand aus der Ansicht selbst.** `PRESENCE` kommt erst nach einer
   * Anmeldung; wer den Einladungslink gerade geöffnet hat, hat noch keine. Ohne diesen Rückfall
   * stünde dort an **jedem** Spieler „nicht verbunden".
   */
  readonly connected?: readonly string[] | undefined;
  /** Gesetzt heißt: dieser Betrachter ist der Host. Fehlt es, gibt es keine Bedienelemente. */
  readonly host?: HostControls | undefined;
}

export function LobbyPanel({ lobby, connected, host }: LobbyPanelProps) {
  const { t } = useTranslation(['lobby', 'sectors', 'status', 'common']);

  /**
   * Zwei Quellen, und die jüngere gewinnt: die lebende Anwesenheit, sonst der Stand, den die
   * Ansicht beim Versand trug.
   */
  const isConnected = (player: { playerId: string; connected: boolean }): boolean =>
    connected === undefined ? player.connected : connected.includes(player.playerId);

  /**
   * Was hinter dem Namen eines Spielers steht — höchstens eine Angabe, oder `null`.
   *
   * **Für einen Bot das Abzeichen, für einen Menschen die Verbindung** (**E64**, Antwort 2), und
   * zwar ausschließend: beides nebeneinander wäre „Bot · nicht verbunden", also genau der
   * Befund, der diese Entscheidung ausgelöst hat.
   */
  const mark = (player: LobbyPlayerDto): ReactNode =>
    player.playerType === 'BOT' ? (
      // Seit `L3a` mit Stufe, Strategie und ⓘ (**E104** Teilfrage 3, **E112**).
      <BotBadge bot={player.bot} />
    ) : isConnected(player) ? null : (
      <span>{t('lobby:slots.disconnected')}</span>
    );
  const freeSectors = lobby.slots
    .filter((slot) => slot.player === undefined)
    .map((slot) => slot.sector);

  return (
    <section>
      <p role="status">{t('lobby:statusLabel', { status: t(`status:${lobby.status}`) })}</p>

      <h3>{t('lobby:slots.heading')}</h3>
      <ul>
        {lobby.slots.map((slot) => (
          <li key={slot.sector}>
            <span>{t(`sectors:${slot.sector}`)}</span>
            {slot.team === undefined ? null : (
              <>
                {' · '}
                <span>{t('lobby:slots.team', { team: slot.team })}</span>
              </>
            )}
            {' · '}
            {slot.player === undefined ? (
              <>
                <span>{t('lobby:slots.free')}</span>
                {host === undefined ? null : (
                  <>
                    {SEPARATOR}
                    {/*
                      **Die Wahl des Bots** (`L2`, **E110**; seit `L4` nur die Stufe, **E127**)
                      — eine Komponente je freiem Platz, damit Name und Wahl des einen nicht im
                      anderen stehen.
                    */}
                    <BotChooser
                      sector={slot.sector}
                      onAdd={(displayName, choice) =>
                        host.onAddBot(slot.sector as SectorIdDto, displayName, choice)
                      }
                    />
                  </>
                )}
              </>
            ) : (
              <>
                <span>{slot.player.displayName}</span>
                {mark(slot.player) === null ? null : (
                  <>
                    {' · '}
                    {mark(slot.player)}
                  </>
                )}
                {host === undefined ? null : (
                  <>
                    {SEPARATOR}
                    <button
                      type="button"
                      onClick={() => host.onAssign(slot.player?.playerId as string, null)}
                    >
                      {t('lobby:host.unassign')}
                    </button>
                    {SEPARATOR}
                    <button
                      type="button"
                      onClick={() => host.onRemove(slot.player?.playerId as string)}
                    >
                      {t('lobby:host.remove')}
                    </button>
                  </>
                )}
              </>
            )}
          </li>
        ))}
      </ul>

      <h3>{t('lobby:waiting.heading')}</h3>
      {lobby.waiting.length === 0 ? (
        <p>{t('lobby:waiting.none')}</p>
      ) : (
        <ul>
          {lobby.waiting.map((player) => (
            <li key={player.playerId}>
              <span>{player.displayName}</span>
              {mark(player) === null ? null : (
                <>
                  {' · '}
                  {mark(player)}
                </>
              )}
              {host === undefined ? null : (
                <>
                  {SEPARATOR}
                  <label>
                    {t('lobby:host.assign')}{' '}
                    <select
                      data-assign={player.playerId}
                      value=""
                      onChange={(event) =>
                        host.onAssign(player.playerId, event.target.value as SectorIdDto)
                      }
                    >
                      {/* Leer bleibt wählbar, damit „noch nichts zugeordnet" ein Zustand ist. */}
                      <option value="">{t('lobby:join.noSector')}</option>
                      {freeSectors.map((sector) => (
                        <option key={sector} value={sector}>
                          {t(`sectors:${sector}`)}
                        </option>
                      ))}
                    </select>
                  </label>
                  {SEPARATOR}
                  <button type="button" onClick={() => host.onRemove(player.playerId)}>
                    {t('lobby:host.remove')}
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      <p>{t('lobby:waiting.hint')}</p>

      {host === undefined ? null : (
        <p>
          <button type="button" onClick={host.onStart} disabled={!lobby.canStart}>
            {t('lobby:host.start')}
          </button>
          {lobby.canStart ? null : ` ${t('lobby:host.cannotStart')}`}{' '}
          <ConfirmButton
            label={t('lobby:host.close')}
            confirmLabel={t('lobby:host.closeConfirm')}
            onConfirm={host.onClose}
          />
        </p>
      )}
    </section>
  );
}
